import {
  type ChangeEvent,
  type ReactElement,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Modal from 'components/Modal/Modal';
import StepItem from 'components/StepItem/StepItem';
import { BUTTON_VARIANT } from 'constants/interfaces';

import {
  agentTypeByCode,
  companyNeedsAgentTemplates,
  type CreateWatchScheduleInput,
  createWatchSchedules,
  duplicateAgentTemplateWarnings,
  enabledTemplatesForType,
  formatHourOfDay,
  MONITORING_AGENT_TYPES,
  SCHEDULE_FREQUENCIES,
  SCHEDULE_TIMEZONES,
} from './scheduleWatchAgents';

import type { WatchTowerMockState } from './types';

import styles from './scss/TargetedMarketing.module.scss';

const LAST_STEP = 4;

interface IWatchTowerCreateScheduleDialog {
  open: boolean,
  presetCompanyId: number,
  state: WatchTowerMockState,
  onClose: () => void,
  onApply: (next: WatchTowerMockState) => void,
  onLiveSave?: (input: CreateWatchScheduleInput) => Promise<void>,
}

function WatchTowerCreateScheduleDialog({
  open,
  presetCompanyId,
  state,
  onClose,
  onApply,
  onLiveSave = undefined,
}: IWatchTowerCreateScheduleDialog): ReactElement {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [agentType, setAgentType] = useState(MONITORING_AGENT_TYPES[0].type);
  const [templateIds, setTemplateIds] = useState<number[]>([]);
  const [hourOfDay, setHourOfDay] = useState(6);
  const [timezone, setTimezone] = useState('Europe/Berlin');
  const [frequency, setFrequency] = useState<string>('daily');
  const [paused, setPaused] = useState(false);
  const [companyIds, setCompanyIds] = useState<number[]>([]);
  const [companySearch, setCompanySearch] = useState('');
  const [onlyWithoutCoverage, setOnlyWithoutCoverage] = useState(false);

  const visibleCompanies = useMemo(
    () => state.watchCompanies.filter((company) => company.status !== 'archived'),
    [state.watchCompanies],
  );
  const templates = enabledTemplatesForType(agentType);
  const duplicates = useMemo(
    () => duplicateAgentTemplateWarnings(state, companyIds, agentType, templateIds),
    [agentType, companyIds, state, templateIds],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    const { type } = MONITORING_AGENT_TYPES[0];
    setStep(1);
    setAgentType(type);
    setTemplateIds([]);
    setHourOfDay(6);
    setTimezone('Europe/Berlin');
    setFrequency('daily');
    setPaused(false);
    setCompanySearch('');
    setOnlyWithoutCoverage(false);
    setCompanyIds(
      visibleCompanies.some((item) => item.id === presetCompanyId)
        ? [presetCompanyId]
        : [],
    );
  }, [open, presetCompanyId, visibleCompanies]);

  const filteredCompanies = visibleCompanies.filter((company) => {
    const haystack = `${company.name} ${company.uid} ${company.addressCountry}`.toLowerCase();
    if (companySearch.trim() && !haystack.includes(companySearch.trim().toLowerCase())) {
      return false;
    }
    if (!onlyWithoutCoverage) {
      return true;
    }
    return companyNeedsAgentTemplates(state.schedules, company.id, agentType, templateIds);
  });

  const canAdvance = () => {
    if (step === 1) {
      return Boolean(agentType);
    }
    if (step === 2) {
      return templateIds.length > 0;
    }
    if (step === 3) {
      return true;
    }
    return companyIds.length > 0;
  };

  const onAgentTypeChange = (type: string) => {
    setAgentType(type);
    setTemplateIds([]);
  };

  const onToggleTemplate = (id: number, checked: boolean) => {
    if (checked) {
      setTemplateIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      return;
    }
    setTemplateIds((prev) => prev.filter((item) => item !== id));
  };

  const onToggleCompany = (id: number, checked: boolean) => {
    if (checked) {
      setCompanyIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      return;
    }
    setCompanyIds((prev) => prev.filter((item) => item !== id));
  };

  const onSave = async () => {
    const agent = agentTypeByCode(agentType);
    const input: CreateWatchScheduleInput = {
      companyIds,
      type: agentType,
      subType: agent?.subType || '',
      agentConfigIds: templateIds,
      timezone,
      hourOfDay,
      frequency,
      paused,
    };
    if (onLiveSave) {
      try {
        await onLiveSave(input);
        toast.success(t('targetedMarketing.scheduleSaved'));
        onClose();
      } catch {
        toast.error(t('targetedMarketing.liveRequestFailed'));
      }
      return;
    }
    const result = createWatchSchedules(state, input);
    if (!result.ok) {
      toast.error(t(`targetedMarketing.scheduleError_${result.error}`));
      return;
    }
    onApply(result.state);
    if (result.skippedCount) {
      toast.success(t('targetedMarketing.scheduleSavedSkipped', {
        created: result.createdIds.length,
        skipped: result.skippedCount,
      }));
    } else {
      toast.success(t('targetedMarketing.scheduleSaved'));
    }
    onClose();
  };

  const onConfirm = () => {
    if (step < LAST_STEP) {
      setStep(step + 1);
      return;
    }
    onSave();
  };

  const confirmTitle = step < LAST_STEP
    ? t('targetedMarketing.createScheduleNext')
    : t('targetedMarketing.saveSchedule');

  return (
    <Modal
      open={open}
      title={t('targetedMarketing.createSchedule')}
      className={`${styles.modalWide} ${styles.wizardModal}`}
      onClose={onClose}
      onReject={onClose}
      onConfirm={onConfirm}
      confirmTitle={confirmTitle}
      closeTitle={t('common.cancel')}
      confirmVariant={BUTTON_VARIANT.primary}
      disabledConfirm={!canAdvance()}
      additionalBtnTitle={step > 1 ? t('targetedMarketing.createScheduleBack') : undefined}
      onActionAdditionalBtn={step > 1 ? () => setStep(step - 1) : undefined}
    >
      <div className={styles.wizardSteps}>
        <StepItem
          title={t('targetedMarketing.createScheduleStepType')}
          step={1}
          done={step > 1}
          disabled={step < 1}
        />
        <StepItem
          title={t('targetedMarketing.createScheduleStepTemplates')}
          step={2}
          done={step > 2}
          disabled={step < 2}
        />
        <StepItem
          title={t('targetedMarketing.createScheduleStepWhen')}
          step={3}
          done={step > 3}
          disabled={step < 3}
        />
        <StepItem
          title={t('targetedMarketing.createScheduleStepCompanies')}
          step={4}
          done={step > 4}
          disabled={step < 4}
        />
      </div>

      {step === 1 ? (
        <fieldset className={`${styles.field} ${styles.fieldWide} ${styles.checkFieldset}`}>
          <legend>{t('targetedMarketing.createSchedulePickType')}</legend>
          <div className={styles.choiceList}>
            {MONITORING_AGENT_TYPES.map((item) => (
              <label
                key={item.type}
                className={`${styles.choiceCard} ${agentType === item.type ? styles.choiceCardActive : ''}`}
                htmlFor={`wt-wiz-type-${item.type}`}
              >
                <input
                  id={`wt-wiz-type-${item.type}`}
                  type="radio"
                  name="wt-wiz-agent-type"
                  checked={agentType === item.type}
                  onChange={() => onAgentTypeChange(item.type)}
                />
                {item.label}
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}

      {step === 2 ? (
        <fieldset className={`${styles.field} ${styles.fieldWide} ${styles.checkFieldset}`}>
          <legend>{t('targetedMarketing.createSchedulePickTemplates')}</legend>
          {templates.length === 0 ? (
            <p className={styles.muted}>{t('targetedMarketing.noEnabledTemplate')}</p>
          ) : (
            <div className={styles.choiceList}>
              {templates.map((item) => (
                <label
                  key={item.id}
                  className={`${styles.choiceCard} ${templateIds.includes(item.id) ? styles.choiceCardActive : ''}`}
                  htmlFor={`wt-wiz-tpl-${item.id}`}
                >
                  <input
                    id={`wt-wiz-tpl-${item.id}`}
                    type="checkbox"
                    checked={templateIds.includes(item.id)}
                    onChange={(event) => onToggleTemplate(item.id, event.target.checked)}
                  />
                  {item.name}
                </label>
              ))}
            </div>
          )}
        </fieldset>
      ) : null}

      {step === 3 ? (
        <div className={styles.formGrid}>
          <p className={`${styles.muted} ${styles.fieldWide}`}>
            {t('targetedMarketing.createSchedulePickWhen')}
          </p>
          <div className={styles.field}>
            <label htmlFor="wt-wiz-hour">{t('targetedMarketing.hourOfDay')}</label>
            <select
              id="wt-wiz-hour"
              value={hourOfDay}
              onChange={(event: ChangeEvent<HTMLSelectElement>) => (
                setHourOfDay(Number(event.target.value))
              )}
            >
              {Array.from({ length: 24 }, (_, hour) => (
                <option key={hour} value={hour}>{formatHourOfDay(hour)}</option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <label htmlFor="wt-wiz-timezone">{t('targetedMarketing.timezone')}</label>
            <select
              id="wt-wiz-timezone"
              value={timezone}
              onChange={(event) => setTimezone(event.target.value)}
            >
              {SCHEDULE_TIMEZONES.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <label htmlFor="wt-wiz-frequency">{t('targetedMarketing.frequency')}</label>
            <select
              id="wt-wiz-frequency"
              value={frequency}
              onChange={(event) => setFrequency(event.target.value)}
            >
              {SCHEDULE_FREQUENCIES.map((item) => (
                <option key={item} value={item}>
                  {t(`targetedMarketing.frequency_${item}`)}
                </option>
              ))}
            </select>
          </div>
          <label className={styles.field} htmlFor="wt-wiz-paused">
            <span>{t('targetedMarketing.paused')}</span>
            <input
              id="wt-wiz-paused"
              type="checkbox"
              checked={paused}
              onChange={(event) => setPaused(event.target.checked)}
            />
          </label>
        </div>
      ) : null}

      {step === 4 ? (
        <fieldset className={`${styles.field} ${styles.fieldWide} ${styles.checkFieldset}`}>
          <legend>{t('targetedMarketing.createSchedulePickCompanies')}</legend>
          <label className={styles.searchField} htmlFor="wt-wiz-company-search">
            <input
              id="wt-wiz-company-search"
              value={companySearch}
              onChange={(event) => setCompanySearch(event.target.value)}
              placeholder={t('targetedMarketing.createScheduleSearchCompanies')}
            />
          </label>
          <label className={styles.checkItem} htmlFor="wt-wiz-only-without">
            <input
              id="wt-wiz-only-without"
              type="checkbox"
              checked={onlyWithoutCoverage}
              onChange={(event) => setOnlyWithoutCoverage(event.target.checked)}
            />
            {t('targetedMarketing.createScheduleOnlyWithout')}
          </label>
          {duplicates.length > 0 ? (
            <div className={styles.warnBanner} role="status">
              <p>{t('targetedMarketing.createScheduleDuplicates', { count: duplicates.length })}</p>
              <ul className={styles.warnList}>
                {duplicates.map((item) => (
                  <li key={`${item.companyId}-${item.templateId}`}>
                    {t('targetedMarketing.createScheduleDuplicateWarn', {
                      name: item.companyName,
                      template: item.templateName,
                    })}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className={styles.checkList}>
            {filteredCompanies.length === 0 ? (
              <p className={styles.muted}>{t('targetedMarketing.emptyCompanies')}</p>
            ) : null}
            {filteredCompanies.map((company) => (
              <label key={company.id} className={styles.checkItem} htmlFor={`wt-wiz-co-${company.id}`}>
                <input
                  id={`wt-wiz-co-${company.id}`}
                  type="checkbox"
                  checked={companyIds.includes(company.id)}
                  onChange={(event) => onToggleCompany(company.id, event.target.checked)}
                />
                {company.name}
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}
    </Modal>
  );
}

WatchTowerCreateScheduleDialog.defaultProps = {
  onLiveSave: undefined,
};

export default WatchTowerCreateScheduleDialog;
